import { ReactNode, useState } from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert, Badge, Banner, Button, DefinitionList, Dialog, EmptyState, Grid, Inline, Link, Menu, PageHeader, Panel, Segmented, Skeleton, Stack, t, Text, toast,
} from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { Shell } from '../../components/Shell';
import { ListTable } from '../../components/ListTable';
import { RefusalPage } from '../../components/Refusal';
import { OutsideScopeBanner, useDisplayScope } from '../../components/ScopePicker';
import { EntryLinks, EnvironmentBadge, notFoundColumn, SecurityBadge, ServerStateDot, serverStateColumn } from '../../components/ServerState';
import { MoreCount } from '../../components/MoreCount';
import { checkColumn } from '../../components/StateColumn';
import { AddressTable } from '../../components/domains/AddressTable';
import { ContainersTable, SitesTable, UnitsTable } from '../../components/RunningPanel';
import type { Container, Running } from '../../components/RunningPanel';
import { DeploymentRulesDialog, ExternalBricksTable, SharedBricksTable } from '../../components/BrickRules';
import { AppSecurityPanels } from '../../components/AppSecurityPanels';
import { TechnologiesDrawer } from '../../components/TechnologiesDrawer';
import { RescanButton } from '../../components/RescanButton';
import { ApplicationRenameDialog, ApplicationRenameProgress } from '../../components/ApplicationRenameDialog';
import { DeployAnalysisDrawer } from '../../components/DeployAnalysisDrawer';
import { DeploymentStatus } from '../../components/DeploymentStatus';
import { MonitoringPanel } from './MonitoringPage';
import { api, ApiFailure, unwrap } from '../../lib/api';
import { owned } from '../../lib/bricks';
import { clockTime, durationSince, relativeTime, shortVersion } from '../../lib/format';
import { RETIRED_STATES, useEnvironmentName } from '../../lib/environments';
import { useSession } from '../../lib/session';

type Application = maintenance.components['schemas']['ApplicationDetail'];
type Deployment = Application['deployments'][number];
type Health = maintenance.components['schemas']['ApplicationHealth'];
type Check = maintenance.components['schemas']['ApplicationCheck'];

const TABS = ['overview', 'deployments', 'health', 'domains', 'bricks', 'monitoring'] as const;
type Tab = (typeof TABS)[number];
const failing = (c: Check) => c.state === 'crit' || c.state === 'warn' || c.state === 'unknown';

/** A portal link: a real href (new tab), client-side navigation on a plain click. */
function RouteLink({ href, children }: { href: string; children: ReactNode }) {
  const navigate = useNavigate();
  return (
    <Link href={href} onClick={(e: { preventDefault: () => void; metaKey?: boolean; ctrlKey?: boolean }) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); navigate(href); }}>
      {children}
    </Link>
  );
}

/** « Tous » and each environment of the application, shared by Santé and Briques (`&env=`). */
function EnvironmentFilter({ environments, value, onChange }: { environments: string[]; value: string | null; onChange: (env: string | null) => void }) {
  const envName = useEnvironmentName();
  return (
    <Segmented
      label={t('application.environments')}
      size="sm"
      value={value && environments.includes(value) ? value : 'all'}
      onChange={id => onChange(id === 'all' ? null : id)}
      items={[{ id: 'all', label: t('application.allEnvironments') }, ...environments.map(id => ({ id, label: envName(id) }))]}
    />
  );
}

/**
 * Application page (SPEC-033, lot 1 since D51), DSM « Record with tabs »
 * (LayoutRecordBand): the band says which application and which section;
 * Vue d'ensemble, Santé, Domaines, Briques and Supervision. Journaux and
 * Déploiements come with their modules (RG-033-5, lot 2). The page reads and
 * orients: each change goes through the screen that already carries it (RG-033-4),
 * except « Modifier › Renommer » (D65), a secondary action of the band.
 */
export function ApplicationPage() {
  const { application = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enums } = useSession();
  const envName = useEnvironmentName();
  const scope = useDisplayScope();
  const asked = params.get('tab') ?? 'overview';
  const tab: Tab = (TABS as readonly string[]).includes(asked) ? asked as Tab : 'overview';
  const env = params.get('env');
  const detail = useQuery({
    queryKey: ['application', application, scope],
    queryFn: async () => unwrap(await api.maintenance.GET('/applications/{application}', { params: { path: { application }, query: { scope } } })),
    refetchInterval: 30_000,
    retry: false,
  });
  const health = useQuery({
    queryKey: ['application-health', application],
    queryFn: async () => unwrap(await api.maintenance.GET('/applications/{application}/health', { params: { path: { application } } })),
    enabled: tab === 'health' && detail.isSuccess,
    refetchInterval: 30_000,
    retry: false,
  });
  // D65: the rename under way (and, at the old address, where the application went).
  const rename = useQuery({
    queryKey: ['application-rename', application],
    queryFn: async () => unwrap(await api.maintenance.GET('/applications/{application}/rename', { params: { path: { application } } })),
    refetchInterval: q => (q.state.data?.pullRequest && !['applied', 'closed'].includes(q.state.data.pullRequest.state) ? 30_000 : false),
    retry: false,
  });
  const [renaming, setRenaming] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [stepUp, setStepUp] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const claim = async (alert: string) => {
    setClaiming(true);
    try {
      unwrap(await api.fleetState.POST('/alerts/{alert}/claim', { params: { path: { alert } } }));
      toast({ tone: 'pos', title: t('alerts.toast.claimed') });
    } catch (e) {
      if (e instanceof ApiFailure) toast({ tone: e.body.code === 'CONFLICT' ? 'info' : 'neg', title: e.body.message, description: e.body.hint });
    } finally {
      setClaiming(false);
      void queryClient.invalidateQueries({ queryKey: ['application', application] });
    }
  };
  /** The tab and the environment live in the URL, so a section can be shared. */
  const go = (next: Tab, environment?: string | null) => {
    const p = new URLSearchParams();
    if (next !== 'overview') p.set('tab', next);
    const e = environment === undefined ? env : environment;
    if (e) p.set('env', e);
    setParams(p);
  };

  // D115: the hierarchy client › projet › application, from the list of projects once the project is known.
  const crumbs = detail.data
    ? [{ label: t('nav.projects'), href: '/projects' }, { label: detail.data.project.title, href: `/projects/${encodeURIComponent(detail.data.project.name)}?tab=applications` }, { label: application }]
    : [{ label: t('nav.applications'), href: '/applications' }, { label: application }];
  if (detail.error) {
    // Renamed (D65): the old address says where the application went, with the link to its page.
    const moved = rename.data?.pullRequest && ['merged', 'applied'].includes(rename.data.pullRequest.state) ? rename.data : null;
    if (moved && detail.error instanceof ApiFailure && detail.error.status === 404) {
      return <Shell crumbs={crumbs} header={<PageHeader band eyebrow={t('page.kind.application')} title={application} />}><ApplicationRenameProgress rename={moved} /></Shell>;
    }
    return <Shell crumbs={crumbs}><RefusalPage error={detail.error} /></Shell>;
  }
  const a = detail.data;
  const label = (list: { value: string; label: string }[], value: string) => list.find(i => i.value === value)?.label ?? value;
  const stateMeta = a?.state ? enums.states.find(s => s.value === a.state) : undefined;
  const environments = a?.deployments.map(d => d.environment) ?? [];

  const header = (
    <PageHeader
      band
      eyebrow={t('page.kind.application')}
      title={application}
      // RG-033-2: the worst of its deployments, said with its environment (« Incident en production »).
      // D118: two indicators: whether it works (the state) and its security (the « Sécurité » badge, as on a server).
      status={a?.state ? (
        <Inline gap={8} align="center" wrap={false}>
          <Badge tone={stateMeta?.tone ?? 'neutral'}>
            {a.stateEnvironment ? t('application.stateIn', { state: stateMeta?.label ?? a.state, environment: envName(a.stateEnvironment).toLocaleLowerCase('fr') }) : stateMeta?.label ?? a.state}
          </Badge>
          {a.security && <SecurityBadge />}
        </Inline>
      ) : undefined}
      subtitle={a ? [
        `${a.client.title} › ${a.project.title}`,
        label(enums.applicationRoles, a.role),
        t('project.team', { team: a.team }),
        t('project.criticality', { criticality: label(enums.criticalities, a.criticality) }),
        a.repository,
      ].filter(Boolean).join(' · ') : undefined}
      actions={a ? (
        <>
          {/* D115: the header acts on the application; a button that only opens a tab is not one (Supervision is a tab). */}
          {/* SPEC-034 (lot 1.5): is its repository deployable by the platform? */}
          {a.repository && <Button variant="secondary" size="sm" icon="fact_check" onClick={() => setAnalysing(true)}>{t('deployContract.action')}</Button>}
          {/* D65: « Renommer », for who may write the application in the catalogue (D48's permission). */}
          {rename.data?.canRename && (
            <Menu
              label={t('application.edit')}
              align="end"
              trigger={<Button variant="secondary" size="sm" icon="edit">{t('application.edit')}</Button>}
              items={[{ label: t('rename.action'), onSelect: () => setRenaming(true) }]}
            />
          )}
        </>
      ) : undefined}
      tabs={{
        label: t('application.tabs.label'),
        value: tab,
        onChange: id => go(id as Tab),
        items: (a?.tabs ?? TABS).map(id => ({
          id, label: t(`application.tabs.${id}`), count: (id === 'health' || id === 'domains' ? a?.tabCounts[id] : undefined) || undefined,
        })),
      }}
    />
  );

  const noDeployment = a && a.deployments.length === 0 && (
    <EmptyState icon="deployed_code" title={t('application.empty.title')}
      action={a.actions.includes('project.servers.update')
        ? <Button variant="secondary" onClick={() => navigate(`/projects/${encodeURIComponent(a.project.name)}/servers`)}>{t('application.empty.servers')}</Button>
        : undefined}>
      {t('application.empty.body')}
    </EmptyState>
  );

  return (
    <Shell crumbs={crumbs} header={header}>
      {!a ? (
        <Stack gap={24}><Skeleton lines={3} /><Skeleton lines={6} /></Stack>
      ) : (
        <Stack gap={24}>
          <OutsideScopeBanner kind="application" outside={a.outsideDisplayScope} />
          {rename.data && <ApplicationRenameProgress rename={rename.data} />}
          {a.stale && <Banner tone="warn" title={t('servers.stale.title')}>{t('servers.stale.body', { when: clockTime(a.generatedAt) })}</Banner>}
          {tab === 'overview' && <Overview a={a} empty={noDeployment} claiming={claiming} onClaim={id => void claim(id)} onHealth={e => go('health', e)} onDomains={() => go('domains')} onDeployments={() => go('deployments')} onBricks={e => go('bricks', e)} />}
          {tab === 'deployments' && <DeploymentsTab a={a} empty={noDeployment} onHealth={e => go('health', e)} />}
          {tab === 'health' && (noDeployment || <HealthTab application={a.name} data={health.data} error={health.error} environments={environments} env={env} onEnv={e => go('health', e)} />)}
          {tab === 'domains' && (
            // D27, RG-033-6: the addresses of its deployments, no « À rattacher » here.
            <Panel title={t('application.domains.title')} subtitle={t('application.domains.subtitle')}>
              <AddressTable application={a.name} />
            </Panel>
          )}
          {tab === 'bricks' && (noDeployment || <BricksTab a={a} env={env} onEnv={e => go('bricks', e)} />)}
          {tab === 'monitoring' && <MonitoringPanel application={a.name} asked={env} onEnvironment={e => go('monitoring', e)} />}
        </Stack>
      )}
      {renaming && rename.data && (
        <ApplicationRenameDialog
          rename={rename.data}
          onClose={() => setRenaming(false)}
          onOpened={() => { setRenaming(false); void rename.refetch(); }}
          onStepUp={() => { setRenaming(false); setStepUp(true); }}
        />
      )}
      {a?.repository && <DeployAnalysisDrawer application={a.name} open={analysing} onClose={() => setAnalysing(false)} />}
      <Dialog
        open={stepUp}
        onClose={() => setStepUp(false)}
        title={t('settings.enrolment.stepUp.title')}
        description={t('applicationRename.stepUp')}
        confirmLabel={t('settings.enrolment.stepUp.confirm')}
        onConfirm={() => window.location.assign(`/api/auth/session/step-up?returnTo=${encodeURIComponent(`/applications/${application}`)}`)}
      />
    </Shell>
  );
}

/** The first server of a deployment, its state and the count of the others; out of scope without link (RG-033-1). */
function ServersCell({ servers }: { servers: Deployment['servers'] }) {
  const [first, ...others] = servers;
  if (!first) return <Text variant="body-sm" tone="mute">—</Text>;
  return (
    <Inline gap={8} align="center" wrap={false}>
      <ServerStateDot state={first.state} />
      {first.visible ? <RouteLink href={`/servers/${encodeURIComponent(first.name)}`}>{first.name}</RouteLink> : <Text variant="code" truncate>{first.name}</Text>}
      <MoreCount n={others.length} title={others.map(s => s.name).join(', ')} />
    </Inline>
  );
}

type Installed = maintenance.operations['listEnvironments']['responses']['200']['content']['application/json']['items'][number];
type Release = maintenance.components['schemas']['InstallationRelease'];
type AppRelease = maintenance.operations['listApplicationReleases']['responses']['200']['content']['application/json']['items'][number];

/** The environments the platform installed for the application's project, the latest installation of each deployment. */
function useInstalled(a: Application) {
  const installed = useQuery({
    queryKey: ['environments', a.project.name],
    queryFn: async () => unwrap(await api.maintenance.GET('/environments', { params: { query: { project: a.project.name } } })),
  });
  const byDeployment = new Map<string, Installed>();
  for (const i of installed.data?.items ?? []) if (i.application === a.name) byDeployment.set(i.deployment, i);
  const installation = (d: Deployment) => byDeployment.get(d.name);
  // SPEC-034 § 12.4: a retired deployment keeps its row, without a version or checks of what no longer runs.
  const retired = (d: Deployment) => {
    const state = installation(d)?.state;
    return state && RETIRED_STATES.includes(state) ? state : null;
  };
  return { installation, retired, items: [...byDeployment.values()], loading: installed.isPending };
}

const hostOfUrl = (url: string) => url.replace(/^https?:\/\//, '').replace(/\/$/, '');

/**
 * Vue d'ensemble (D111): what needs an action, each with the way to the screen that treats it, or « Tout est en
 * ordre »; its properties. The deployments themselves are the « Déploiements » tab.
 */
function Overview({ a, empty, claiming, onClaim, onHealth, onDomains, onDeployments, onBricks }: {
  a: Application; empty: ReactNode; claiming: boolean; onClaim: (alert: string) => void; onHealth: (env: string) => void; onDomains: () => void; onDeployments: () => void; onBricks: (env: string) => void;
}) {
  const { enums } = useSession();
  const envName = useEnvironmentName();
  const navigate = useNavigate();
  const label = (list: { value: string; label: string }[], value: string) => list.find(i => i.value === value)?.label ?? value;
  const canClaim = a.actions.includes('alert.claim');
  const { installation, retired } = useInstalled(a);
  const live = a.deployments.filter(d => !retired(d));
  const notServed = live.filter(d => d.url && d.foundOnServer === false);
  const openEnvironment = (d: Deployment) => navigate(`/environments/${encodeURIComponent(d.name)}`);
  // One line per server in trouble, whatever the number of its deployments.
  const servers = new Map<string, { state: string; environment: string }>();
  for (const d of live) for (const s of d.servers) if ((s.state === 'incident' || s.state === 'to_update') && s.visible && !servers.has(s.name)) servers.set(s.name, { state: s.state, environment: d.environment });
  const production = a.deployments.find(d => d.environmentType === 'production' && !retired(d));

  const items: ReactNode[] = [
    ...a.alerts.map(al => (
      <Alert
        key={`alert:${al.id}`}
        tone={al.claimedBy ? 'info' : al.severity === 'warning' ? 'warn' : 'neg'}
        title={t('application.alert.title', { service: al.service, environment: envName(al.environment), server: al.server, since: clockTime(al.startedAt) })}
        action={!al.claimedBy && canClaim && !claiming ? t('alerts.claim') : undefined}
        onAction={() => onClaim(al.id)}
      >
        {[al.output, al.claimedBy ? t('alerts.claimedBy', { name: al.claimedBy.name, time: clockTime(al.claimedAt) }) : t('alerts.notClaimed')].join(' · ')}
      </Alert>
    )),
    ...live.flatMap(d => {
      const i = installation(d);
      const out: ReactNode[] = [];
      if (i?.state === 'failed') {
        out.push(<Alert key={`failed:${d.name}`} tone="neg" action={t('application.summary.openEnvironment')} onAction={() => openEnvironment(d)} title={t('application.summary.failed', { environment: envName(d.environment) })} />);
      } else if (i?.observed === 'degraded' || i?.observed === 'gap') {
        out.push(<Alert key={`observed:${d.name}`} tone={i.observed === 'degraded' ? 'neg' : 'warn'} action={t('application.summary.openEnvironment')} onAction={() => openEnvironment(d)}
          title={t(`application.summary.${i.observed}`, { environment: envName(d.environment) })} />);
      }
      if (d.checks && d.checks.failing > 0) {
        out.push(<Alert key={`checks:${d.name}`} tone="neg" action={t('application.summary.openHealth')} onAction={() => onHealth(d.environment)}
          title={t('application.summary.checks', { n: d.checks.failing, environment: envName(d.environment) })} />);
      }
      return out;
    }),
    ...[...servers.entries()].map(([server, s]) => (
      <Alert key={`server:${server}`} tone={s.state === 'incident' ? 'neg' : 'warn'} action={t('application.summary.openServer')}
        onAction={() => navigate(`/servers/${encodeURIComponent(server)}?tab=health`)}
        title={t(`application.summary.server.${s.state}`, { server, environment: envName(s.environment) })} />
    )),
    // D118: its application defects (end of life, critical vulnerabilities of an image, bricks in fault).
    ...a.defects.map(d => (
      <Alert key={`defect:${d.deployment}:${d.code}:${d.label}`} tone={d.code === 'RUNTIME_EOL' ? 'warn' : 'neg'} action={t('application.summary.openBricks')} onAction={() => onBricks(d.environment)} title={d.label} />
    )),
    ...a.certificates.map(c => (
      <Alert key={`cert:${c.host}`} tone={c.daysLeft < 7 ? 'neg' : 'warn'} action={t('application.domains.open')} onAction={onDomains}
        title={c.daysLeft < 0 ? t('application.certificate.expired', { host: c.host, environment: envName(c.environment) }) : t('application.certificate.expiring', { host: c.host, environment: envName(c.environment), n: c.daysLeft })} />
    )),
    ...notServed.map(d => (
      <Alert key={`served:${d.name}`} tone="warn" action={t('application.domains.open')} onAction={onDomains}
        title={t('application.notServed', { host: hostOfUrl(d.url!), environment: envName(d.environment) })} />
    )),
  ];
  return (
    <Grid columns="2-1">
      <Panel title={t('application.summary.title')} subtitle={t('application.summary.subtitle', { n: live.length })}>
        {empty || (items.length
          ? <Stack gap={12}>{items}</Stack>
          : <Alert tone="pos" title={t('application.summary.allGood')} action={t('application.summary.openDeployments')} onAction={onDeployments}>{t('application.summary.allGoodBody', { n: live.length })}</Alert>)}
      </Panel>
      <Panel title={t('application.properties')}>
        <DefinitionList
          stacked
          items={[
            { label: t('application.props.project'), value: <RouteLink href={`/projects/${encodeURIComponent(a.project.name)}`}>{a.project.title}</RouteLink> },
            { label: t('application.props.client'), value: <RouteLink href={`/clients/${encodeURIComponent(a.client.name)}`}>{a.client.title}</RouteLink> },
            { label: t('application.props.role'), value: label(enums.applicationRoles, a.role) },
            { label: t('application.props.team'), value: a.team },
            { label: t('application.props.criticality'), value: label(enums.criticalities, a.criticality) },
            { label: t('application.props.repository'), value: a.repository ? <Link href={`https://github.com/${a.repository}`} external>{a.repository}</Link> : null },
            // D111: the version in service in production, once the platform installed it.
            { label: t('application.props.production'), value: production && installation(production)?.release ? <Text variant="code">{shortVersion(installation(production)!.release!)}</Text> : null },
            { label: t('application.props.contacts'), value: <RouteLink href={`/clients/${encodeURIComponent(a.client.name)}`}>{t('application.props.contactsLink')}</RouteLink> },
          ]}
        />
      </Panel>
    </Grid>
  );
}

const RELEASE_TONE: Record<Release['state'], 'pos' | 'warn' | 'neg' | 'neutral' | 'info'> = {
  running: 'info', succeeded: 'pos', rolled_back_to: 'pos', failed_rolled_back: 'warn', failed_rollback_proposed: 'neg', failed_rollback_failed: 'neg', failed: 'neg',
};

/**
 * Déploiements (D111, lot 1.5): one row per environment, production first, with its status; then the latest
 * releases of every environment the platform installed. The history of GitHub runs stays in lot 2 (E15).
 */
function DeploymentsTab({ a, empty, onHealth }: { a: Application; empty: ReactNode; onHealth: (env: string) => void }) {
  const { enums } = useSession();
  const envName = useEnvironmentName();
  const navigate = useNavigate();
  const { installation, retired, items } = useInstalled(a);
  return (
    <Stack gap={24}>
      <Panel title={t('application.deployments.title')} subtitle={t('application.deployments.subtitle')} flush>
        {empty || (
          // D120: the server searches, filters, sorts and pages; the installed deployments add their status.
          <ListTable<Deployment>
            label={t('application.deployments.title')}
            queryKey={['application-deployments', a.name]}
            fetch={async query => unwrap(await api.maintenance.GET('/applications/{application}/deployments', { params: { path: { application: a.name }, query: query as never } }))}
            rowKey="name"
            defaultSort={{ key: 'order', dir: 'asc' }}
            search={t('application.deployments.search')}
            facets={[
              { key: 'state', label: t('list.facets.state'), valueLabel: v => enums.states.find(x => x.value === v)?.label ?? v },
              { key: 'environment', label: t('application.deployments.environment'), valueLabel: envName },
              { key: 'server', label: t('application.deployments.servers') },
            ]}
            onRowClick={(d: Deployment) => (installation(d) ? navigate(`/environments/${encodeURIComponent(d.name)}`) : onHealth(d.environment))}
            columns={[
              serverStateColumn<Deployment>(enums, d => ({ state: d.state, retired: retired(d) })),
              { key: 'environment', label: t('application.deployments.environment'), pinned: true, width: 150, accessor: (d: Deployment) => envName(d.environment),
                render: (d: Deployment) => <EnvironmentBadge environment={d.environment} type={d.environmentType} /> },
              // D96: the same status as the environment's page.
              { key: 'status', label: t('application.deployments.status'), width: 150, sortable: false, render: (d: Deployment) => {
                const i = installation(d);
                return i ? <DeploymentStatus state={i.state} observed={i.observed} size="sm" /> : <Text variant="body-sm" tone="mute">—</Text>;
              } },
              { key: 'version', label: t('application.deployments.version'), width: 110, sortable: false, accessor: (d: Deployment) => (retired(d) ? '—' : d.version ?? (installation(d)?.release ? shortVersion(installation(d)!.release!) : '—')) },
              { key: 'servers', label: t('application.deployments.servers'), width: 240, sortable: false, render: (d: Deployment) => <ServersCell servers={d.servers} /> },
              // RG-032-2: the declared address; D58: when no site of its servers serves it, said in the next column.
              // D109: its entry points when the repository declares them, else its declared address.
              { key: 'url', label: t('application.deployments.url'), grow: true, width: 240, sortable: false, render: (d: Deployment) => (installation(d)?.entryPoints.length
                ? <EntryLinks entryPoints={installation(d)!.entryPoints} />
                : d.url ? <Link href={d.url} external><Text variant="code" truncate>{hostOfUrl(d.url)}</Text></Link> : <Text variant="body-sm" tone="mute">—</Text>) },
              notFoundColumn<Deployment>(),
              { key: 'checks', label: t('application.deployments.health'), width: 190, sortable: false, render: (d: Deployment) => (d.checks && !retired(d)
                // Failing in red; not run by Checkmk yet, said apart (never as a failure).
                ? <Text variant="body-sm" tone={d.checks.failing > 0 ? 'neg' : d.checks.pending > 0 ? 'mute' : undefined} truncate>
                    {d.checks.failing > 0 ? t('application.deployments.checksFailing', { n: d.checks.failing, total: d.checks.total })
                      : d.checks.pending > 0 ? t('application.deployments.checksPending', { n: d.checks.pending, total: d.checks.total })
                        : t('application.deployments.checks', { ok: d.checks.ok, n: d.checks.total })}
                  </Text>
                : <Text variant="body-sm" tone="mute">—</Text>) },
            ]}
            empty={<EmptyState framed={false} icon="apps" title={t('application.deployments.empty')} />}
          />
        )}
      </Panel>
      {items.length > 0 && (
        <Panel title={t('application.releases.title')} subtitle={t('application.releases.subtitle')} flush>
          {/* D120: every version deployed, searched, filtered and paged by the server; the newest first. */}
          <ListTable<AppRelease>
            label={t('application.releases.title')}
            queryKey={['application-releases', a.name]}
            fetch={async query => unwrap(await api.maintenance.GET('/applications/{application}/releases', { params: { path: { application: a.name }, query: query as never } }))}
            rowKey="id"
            defaultSort={{ key: 'createdAt', dir: 'desc' }}
            search={t('application.releases.search')}
            facets={[
              { key: 'environment', label: t('application.deployments.environment'), valueLabel: envName },
              { key: 'state', label: t('environment.versions.result'), valueLabel: v => t(`environment.versions.states.${v}`) },
              { key: 'kind', label: t('environment.versions.kind'), valueLabel: v => t(`environment.versions.kinds.${v}`) },
            ]}
            onRowClick={r => navigate(`/environments/${encodeURIComponent(r.deployment)}?tab=versions`)}
            empty={<EmptyState framed={false} icon="history" title={t('environment.versions.empty')} />}
            columns={[
              { key: 'environment', label: t('application.deployments.environment'), width: 150, accessor: (r: AppRelease) => envName(r.environment) },
              { key: 'version', label: t('environment.versions.version'), grow: true, render: (r: AppRelease) => (
                <Inline gap={8}>
                  <Text variant="code">{shortVersion(r.version)}</Text>
                  {r.current && <Badge tone="brand" size="sm">{t('environment.versions.current')}</Badge>}
                </Inline>
              ) },
              { key: 'kind', label: t('environment.versions.kind'), width: 140, sortable: false, render: (r: AppRelease) => t(`environment.versions.kinds.${r.kind}`) },
              { key: 'state', label: t('environment.versions.result'), width: 220, sortable: false, render: (r: AppRelease) => (
                <Stack gap={4}>
                  <Badge tone={RELEASE_TONE[r.state]} size="sm">{t(`environment.versions.states.${r.state}`)}</Badge>
                  {r.failure && <Text variant="caption" tone="mute">{r.failure}</Text>}
                </Stack>
              ) },
              { key: 'createdAt', label: t('environment.versions.when'), width: 220, render: (r: AppRelease) => `${relativeTime(r.createdAt)} · ${r.createdBy.replace(/^user:default\//, '')}` },
            ]}
          />
        </Panel>
      )}
    </Stack>
  );
}


/** Santé: its failing checks first, then the ones in order by environment, then the servers that carry it. */
function HealthTab({ application, data, error, environments, env, onEnv }: { application: string; data: Health | undefined; error: unknown; environments: string[]; env: string | null; onEnv: (env: string | null) => void }) {
  const navigate = useNavigate();
  const envName = useEnvironmentName();
  const { enums } = useSession();
  if (error) return <Alert tone="neg" title={error instanceof ApiFailure ? error.body.message : t('errors.retry')}>{error instanceof ApiFailure ? error.body.hint : null}</Alert>;
  const shown = env && environments.includes(env) ? env : null;
  // The counts of the panels' titles; the rows are the lists below (D120: searched, filtered and paged by the server).
  const checks = (data?.checks ?? []).filter(c => !shown || c.environment === shown);
  const toFix = checks.filter(failing);
  const fine = checks.filter(c => !failing(c));
  // A check never run yet is not « en ordre »: it says nothing (Checkmk has not applied it, or not run it yet).
  const untested = fine.filter(c => !c.lastCheckAt).length;
  const name = (c: Check) => `${t(`monitoring.types.${c.type}`)} ${c.target.replace(/^https?:\/\//, '')}`;
  const onEnvironment: Record<string, string> = shown ? { environment: shown } : {};
  const fetchChecks = async (query: Record<string, unknown>) => unwrap(await api.maintenance.GET('/applications/{application}/health/checks', { params: { path: { application }, query: query as never } }));
  type Server = Health['servers'][number];
  const checkFacets = [
    { key: 'server', label: t('application.health.server') },
    { key: 'type', label: t('application.health.check'), valueLabel: (v: string) => t(`monitoring.types.${v}`) },
  ];
  return (
    <Stack gap={24}>
      <EnvironmentFilter environments={environments} value={shown} onChange={onEnv} />
      <Panel title={t('health.toFix')} subtitle={data ? t('application.health.subtitle', { when: relativeTime(data.lastDataAt) }) : undefined} flush>
        {!data ? <Skeleton lines={4} />
          : !checks.length ? <EmptyState framed={false} icon="monitor_heart" title={t('application.health.empty.title')}>{t('application.health.empty.body')}</EmptyState>
            : toFix.length > 0 ? (
              <ListTable<Check>
                label={t('health.toFix')}
                queryKey={['application-health-checks', application, 'failing', shown]}
                fetch={fetchChecks}
                fixed={{ status: 'failing', ...onEnvironment }}
                rowKey="id"
                defaultSort={{ key: 'state', dir: 'asc' }}
                search={t('health.search')}
                facets={checkFacets}
                columns={[
                  checkColumn<Check>(c => c),
                  { key: 'name', label: t('application.health.check'), pinned: true, strong: true, width: 300, accessor: name },
                  { key: 'environment', label: t('application.deployments.environment'), width: 140, accessor: (c: Check) => envName(c.environment) },
                  { key: 'server', label: t('application.health.server'), mono: true, width: 180 },
                  { key: 'output', label: t('health.cause'), grow: true, width: 280, sortable: false, accessor: (c: Check) => c.output ?? '—' },
                  { key: 'since', label: t('health.since'), width: 110, render: (c: Check) => (c.since ? durationSince(c.since) : '—') },
                ]}
                empty={<Alert tone="pos" title={t('health.allGood', { n: checks.length })} />}
              />
            ) : untested ? <Alert tone="info" title={t('health.untested', { n: untested, total: checks.length })} />
              : <Alert tone="pos" title={t('health.allGood', { n: checks.length })} />}
      </Panel>
      {fine.length > 0 && (
        <Panel title={t('health.inOrder')} subtitle={t('health.familyCount', { n: fine.length })} flush>
          <ListTable<Check>
            label={t('health.inOrder')}
            queryKey={['application-health-checks', application, 'fine', shown]}
            fetch={async query => {
              // In order, or not run yet (never in red): every check that is not failing.
              const asked = (query.filter ?? {}) as Record<string, string>;
              return fetchChecks({ ...query, filter: { ...asked, status: asked.status ?? 'fine,untested' } });
            }}
            fixed={onEnvironment}
            rowKey="id"
            defaultSort={{ key: 'environment', dir: 'asc' }}
            search={t('health.search')}
            facets={[{ key: 'environment', label: t('application.deployments.environment'), valueLabel: envName }, ...checkFacets]}
            columns={[
              checkColumn<Check>(c => c),
              { key: 'name', label: t('application.health.check'), pinned: true, width: 320, accessor: name },
              { key: 'environment', label: t('application.deployments.environment'), width: 140, accessor: (c: Check) => envName(c.environment) },
              { key: 'target', label: t('application.health.target'), mono: true, grow: true, width: 280, sortable: false },
              { key: 'server', label: t('application.health.server'), mono: true, width: 180 },
              { key: 'lastCheckAt', label: t('server.health.columns.lastCheck'), width: 140,
                render: (c: Check) => (c.lastCheckAt ? relativeTime(c.lastCheckAt) : t('checks.notYet')) },
            ]}
            empty={<EmptyState framed={false} icon="monitor_heart" title={t('application.health.empty.title')} />}
          />
        </Panel>
      )}
      <Panel title={t('application.health.servers')} subtitle={t('application.health.serversSubtitle')} flush>
        <ListTable<Server>
          label={t('application.health.servers')}
          queryKey={['application-health-servers', application, shown]}
          fetch={async query => unwrap(await api.maintenance.GET('/applications/{application}/health/servers', { params: { path: { application }, query: query as never } }))}
          fixed={onEnvironment}
          rowKey="name"
          defaultSort={{ key: 'state', dir: 'asc' }}
          search={t('application.health.serversSearch')}
          facets={[{ key: 'state', label: t('list.facets.state'), valueLabel: v => enums.states.find(x => x.value === v)?.label ?? v }]}
          onRowClick={(s: Server) => { if (s.visible) navigate(`/servers/${encodeURIComponent(s.name)}?tab=health`); }}
          columns={[
            serverStateColumn<Server>(enums, s => ({ state: s.state, reason: s.reasons[0]?.label })),
            { key: 'name', label: t('application.health.server'), mono: true, strong: true, pinned: true, width: 200 },
            { key: 'environments', label: t('application.environments'), width: 200, sortable: false,
              render: (s: Server) => <Inline gap={4} wrap={false}>{s.environments.map(e => <EnvironmentBadge key={e} environment={e} />)}</Inline> },
            { key: 'reasons', label: t('server.causes'), grow: true, width: 320, sortable: false, render: (s: Server) => (s.reasons.length ? (
              <Inline gap={8} align="center" wrap={false}>
                <Text variant="body-sm" truncate>{s.reasons[0]!.label}</Text>
                <MoreCount n={s.reasons.length - 1} title={s.reasons.slice(1).map(r => r.label).join(' · ')} />
              </Inline>
            ) : <Text variant="body-sm" tone="mute" truncate>{s.visible ? '—' : t('application.health.outOfScope')}</Text>) },
          ]}
          empty={<EmptyState framed={false} icon="dns" title={t('project.deployment.noServer')} />}
        />
      </Panel>
    </Stack>
  );
}

/** The worse an external brick's check, the higher. */
const BRICK_GRAVITY: Record<Running['external'][number]['state'], number> = { ok: 0, pending: 1, warn: 2, fault: 3 };

/**
 * Briques (D51): what runs for each deployment, server by server — its sites,
 * containers (with their technologies, D46), services and cron jobs, external
 * and shared bricks (D48) — with the server page's tables, read from
 * `/servers/{server}/running` (the person's scope applies). A container opens
 * its technologies in detail; « Modifier les règles » as on the server page.
 */

function BricksTab({ a, env, onEnv }: { a: Application; env: string | null; onEnv: (env: string | null) => void }) {
  const { can } = useSession();
  const envName = useEnvironmentName();
  const queryClient = useQueryClient();
  const [opened, setOpened] = useState<Container | null>(null);
  const [openedOn, setOpenedOn] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ server: string; deployment: string; title: string } | null>(null);
  const environments = a.deployments.map(d => d.environment);
  const shown = env && environments.includes(env) ? env : null;
  const deployments = a.deployments.filter(d => !shown || d.environment === shown);
  // A server shared out of scope is not read (RG-033-1): its name only.
  const names = [...new Set(deployments.flatMap(d => d.servers.filter(s => s.visible).map(s => s.name)))];
  const readings = useQueries({
    queries: names.map(server => ({
      queryKey: ['server-running', server],
      queryFn: async () => unwrap(await api.fleetState.GET('/servers/{server}/running', { params: { path: { server } } })),
      refetchInterval: 60_000,
      retry: false,
    })),
  });
  const running = new Map(names.map((name, i) => [name, readings[i]!]));
  // D48: « Modifier les règles », the permission of the server page's « Déclarer une application ».
  const canEdit = can('project.servers.update');

  return (
    <Stack gap={24}>
      <Inline gap={12} align="center" wrap>
        <EnvironmentFilter environments={environments} value={shown} onChange={onEnv} />
        <Text variant="body-sm" tone="mute">{t('application.bricks.lead')}</Text>
      </Inline>
      {deployments.map(d => {
        const environment = envName(d.environment);
        // External and shared bricks are the deployment's: read on each of its servers, said once.
        const external = new Map<string, Running['external'][number]>();
        const shared = new Map<string, Running['shared'][number]>();
        for (const s of d.servers) {
          const r = running.get(s.name)?.data;
          // Each server runs its own check of the brick: the worst one is said.
          r?.external.filter(e => e.deployment === d.name).forEach(e => {
            const kept = external.get(e.name);
            if (!kept || BRICK_GRAVITY[e.state] > BRICK_GRAVITY[kept.state]) external.set(e.name, e);
          });
          r?.shared.filter(l => l.owner.deployment === d.name || l.user.deployment === d.name).forEach(l => shared.set(`${l.owner.deployment}/${l.brick}>${l.user.deployment}`, l));
        }
        return (
          <Stack key={d.name} gap={16}>
            {d.servers.map(server => {
              const query = running.get(server.name);
              const r = query?.data;
              const mine = r ? owned(d.name, r.sites, r.containers, r.units ?? []) : null;
              const read = r?.readAt ? t('running.readAt', { when: relativeTime(r.readAt) }) : undefined;
              const nothing = mine && !mine.sites.length && !mine.containers.length && !mine.units.length;
              const named = { application: a.name, environment, server: server.name };
              return (
                <Panel
                  key={server.name}
                  eyebrow={environment}
                  title={server.name}
                  subtitle={read}
                  actions={server.visible && r?.installed && (canEdit || r.canRescan) ? (
                    <Inline gap={8}>
                      {/* D55: Trivy analyses the server's running images now. */}
                      <RescanButton server={server.name} running={r} />
                      {canEdit && <Button variant="secondary" size="sm" icon="tune" onClick={() => setEditing({ server: server.name, deployment: d.name, title: `${a.name} · ${environment}` })}>{t('bricks.rules.open')}</Button>}
                    </Inline>
                  ) : undefined}
                >
                  {!server.visible ? <Text variant="body-sm" tone="mute">{t('application.bricks.outOfScope')}</Text>
                    : !query || query.isPending ? <Skeleton lines={3} />
                      : !r ? <Text variant="body-sm" tone="mute">{query.error instanceof ApiFailure ? query.error.body.message : t('running.failed')}</Text>
                        : !r.installed ? <Text variant="body-sm" tone="mute">{r.pending ? t('running.pending.title') : t('running.notInstalled.title')}</Text>
                          : (
                            <Stack gap={16}>
                              {mine!.sites.length > 0 && <SitesTable label={t('application.bricks.sites', named)} server={server.name} deployment={d.name} wordpress={r.wordpress} />}
                              {mine!.containers.length > 0 && (
                                <ContainersTable label={t('application.bricks.containers', named)} server={server.name} deployment={d.name} onOpen={c => { setOpened(c); setOpenedOn(server.name); }} active={opened?.name ?? null} />
                              )}
                              {mine!.units.length > 0 && <UnitsTable label={t('application.bricks.units', named)} server={server.name} deployment={d.name} />}
                              {!r.restricted && <AppSecurityPanels server={server.name} only={{ wordpress: { domain: [...mine!.domains].join(',') }, php: false }} />}
                              {nothing && <EmptyState framed={false} icon="deployed_code" title={t('applications.nothingRead')}>{t('applications.nothingReadBody')}</EmptyState>}
                            </Stack>
                          )}
                </Panel>
              );
            })}
            {(external.size > 0 || shared.size > 0) && (
              <Panel eyebrow={environment} title={t('application.bricks.linked')} subtitle={t('application.bricks.linkedSubtitle')}>
                <Stack gap={16}>
                  {external.size > 0 && <ExternalBricksTable label={t('application.bricks.external', { application: a.name, environment })} deployment={d.name} />}
                  {shared.size > 0 && <SharedBricksTable label={t('application.bricks.shared', { application: a.name, environment })} deployment={d.name} environmentLabel={envName} />}
                </Stack>
              </Panel>
            )}
          </Stack>
        );
      })}
      <TechnologiesDrawer server={openedOn} container={opened} onClose={() => setOpened(null)} />
      {editing && (
        <DeploymentRulesDialog
          server={editing.server}
          deployment={editing.deployment}
          title={editing.title}
          onClose={() => setEditing(null)}
          onOpened={() => { setEditing(null); void queryClient.invalidateQueries({ queryKey: ['server-running', editing.server] }); }}
        />
      )}
    </Stack>
  );
}
